// Owner HQ — full god-mode dashboard with editing for everything
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { renderSafeMarkdown } from "../lib/safeMarkdown";
import DashboardQuickActions from "../components/DashboardQuickActions";
import { Crown, Activity, Users, DollarSign, Zap, Power, Plug, Megaphone, Sparkles, Edit3, Trash2, Plus, X, Hexagon, Settings, Trophy, Tv, Gift, Shield, Wrench, Radio, Bell, Mail, AlertTriangle, AlertCircle, Smartphone, BookOpen, Headphones, Bot, Target, ChevronDown } from "lucide-react";
import { toast, Toaster } from "sonner";
import { UsersTab, SponsorPoolTab, GrantPanel, OwnerMobileGoLive, Modal } from "./owner/UserAdminTabs";
import { MoneyFlowTab } from "./owner/MoneyFlowTab";
import OwnerBuilderTab from "./owner/OwnerBuilderTab";
import OwnerEconomyTab from "./owner/OwnerEconomyTab";
import OwnerXPTab from "./owner/OwnerXPTab";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";

const TABS = [
  { id: "overview", label: "Overview", icon: Activity },
  { id: "builder", label: "AI Builder", icon: Bot },
  { id: "economy", label: "Economy", icon: DollarSign },
  { id: "xp", label: "XP & Levels", icon: Trophy },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "tutorials", label: "Tutorials & Files", icon: BookOpen },
  { id: "support", label: "Customer Service", icon: Headphones },
  { id: "marketing", label: "Marketing Assets", icon: Megaphone },
  { id: "email", label: "Bulk Email", icon: Mail },
  { id: "flagged", label: "Flagged Users", icon: AlertTriangle },
  { id: "integrations", label: "Integrations", icon: Plug },
  { id: "streams", label: "Streams", icon: Tv },
  { id: "stickers", label: "Stickers", icon: Sparkles },
  { id: "pricing", label: "Pricing", icon: DollarSign },
  { id: "users", label: "Users", icon: Users },
  { id: "sponsor_pool", label: "Sponsor Pool", icon: Trophy },
  { id: "money_flow", label: "Money Flow", icon: DollarSign },
  { id: "ad_escrows", label: "Ad Escrows", icon: Target },
  { id: "flags", label: "Feature Flags", icon: Power },
  { id: "takeovers", label: "Takeovers", icon: Megaphone },
  { id: "raid", label: "Owner Raid", icon: Radio },
  { id: "sponsored", label: "Sponsored Takeovers", icon: Trophy },
  { id: "content", label: "BReels / Merch", icon: Gift },
  { id: "drops", label: "Coin Drops", icon: Sparkles },
  { id: "settings", label: "Platform Settings", icon: Settings },
  { id: "tech", label: "Tech Console", icon: Wrench },
  { id: "health", label: "Health Check", icon: Activity },
];

export default function OwnerHQ() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");

  if (!user) return <div className="p-20 text-center text-white/40">Sign in as owner.</div>;
  if (user.role !== "owner") return <div className="p-20 text-center text-white/40"><Shield className="w-12 h-12 mx-auto mb-4 text-nexus-amber" />Owner HQ is restricted. Sign in as owner@nexus.io.</div>;

  return (
    <div className="surface-light max-w-[1500px] mx-auto space-y-8 relative" data-testid="owner-hq">
      <Toaster theme="dark" position="top-center" />

      <div className="flex items-center gap-4">
        <Crown className="w-10 h-10 text-nexus-amber animate-pulse-glow" />
        <div>
          <div className="label-eyebrow mb-1 text-nexus-amber">// owner hq · god mode</div>
          <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tighter">NexusLive, you have the wheel.</h1>
          <DashboardQuickActions className="mt-3" />
        </div>
      </div>

      {/* TABS */}
      <div className="flex flex-wrap gap-1 border-b border-white/10 pb-1 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-4 py-2 text-[10px] tracking-[0.24em] uppercase font-display flex items-center gap-2 transition-colors whitespace-nowrap ${tab === t.id ? "text-nexus-cyan border-b-2 border-nexus-cyan" : "text-[#0f0f12]/60 hover:text-[#0f0f12]"}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      <div className="min-h-[400px]">
        {tab === "overview" && <Overview />}
        {tab === "builder" && <OwnerBuilderTab />}
        {tab === "economy" && <OwnerEconomyTab />}
        {tab === "xp" && <OwnerXPTab />}
        {tab === "notifications" && <NotificationsTab />}
        {tab === "tutorials" && <TutorialsTab />}
        {tab === "support" && <SupportTab />}
        {tab === "marketing" && <MarketingTab />}
        {tab === "email" && <BulkEmailTab />}
        {tab === "flagged" && <FlaggedUsersTab />}
        {tab === "integrations" && <Integrations />}
        {tab === "streams" && <Streams />}
        {tab === "stickers" && <Stickers />}
        {tab === "pricing" && <Pricing />}
        {tab === "users" && <UsersTab />}
        {tab === "sponsor_pool" && <SponsorPoolTab />}
        {tab === "money_flow" && <MoneyFlowTab />}
        {tab === "ad_escrows" && <AdEscrowsTab />}
        {tab === "flags" && <Flags />}
        {tab === "takeovers" && <Takeovers />}
        {tab === "raid" && <OwnerRaid />}
        {tab === "sponsored" && <SponsoredTakeovers />}
        {tab === "content" && <Content />}
        {tab === "drops" && <CoinDrops />}
        {tab === "settings" && <PlatformSettings />}
        {tab === "tech" && <TechConsole />}
        {tab === "health" && <HealthCheckTab />}
      </div>
    </div>
  );
}

/* ============================== OVERVIEW ============================== */
function Overview() {
  const [m, setM] = useState(null);
  const [rev, setRev] = useState(null);
  const [liveStreams, setLiveStreams] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ed3nOpen, setEd3nOpen] = useState(false);
  const { user } = useAuth();
  useEffect(() => {
    api.get("/owner/metrics").then(({ data }) => setM(data));
    api.get("/owner/revenue").then(({ data }) => setRev(data));
    api.get("/streams", { params: { live_only: true } }).then(({ data }) => setLiveStreams(data));
  }, []);
  if (!m || !rev) return <div className="text-white/40">Loading live revenue feed…</div>;

  return (
    <div className="space-y-8" data-testid="overview-tab">
      {/* OWNER QUICK-ACTION BAR */}
      <div className="glass-strong p-4 flex flex-wrap items-center gap-3 border-l-2 border-nexus-amber">
        <div className="flex-1 min-w-[200px]"><div className="label-eyebrow text-nexus-amber">// owner all-access bar</div><div className="text-sm text-white/70 mt-0.5">Studio tier free. Mobile + laptop go-live. Jump to any dashboard.</div></div>
        <button onClick={() => setMobileOpen(true)} className="btn-nexus" data-testid="owner-mobile-golive"><Smartphone className="w-3.5 h-3.5" />Go Live (Mobile)</button>
        <button onClick={() => setEd3nOpen(true)} className="btn-nexus !border-nexus-violet" data-testid="eden-fab"><Bot className="w-3.5 h-3.5" />ED3N AI</button>
        <Link to="/studio" className="btn-ghost text-[10px]" data-testid="jump-studio"><Radio className="w-3 h-3" />Studio</Link>
        <Link to="/sponsor" className="btn-ghost text-[10px]" data-testid="jump-sponsor"><Megaphone className="w-3 h-3" />Sponsor</Link>
        <Link to="/staff" className="btn-ghost text-[10px]" data-testid="jump-staff"><Shield className="w-3 h-3" />Staff</Link>
        <Link to="/explore" className="btn-ghost text-[10px]"><Tv className="w-3 h-3" />Explore</Link>
        <Link to={user ? `/u/${user.id}` : "/auth?owner=1"} className="btn-ghost text-[10px]"><Crown className="w-3 h-3" />My Profile</Link>
      </div>
      {mobileOpen && <OwnerMobileGoLive onClose={() => setMobileOpen(false)} />}
      {ed3nOpen && <OwnerEd3nPanel onClose={() => setEd3nOpen(false)} />}
      {/* TOP REVENUE BANNER */}
      <div className="grid lg:grid-cols-4 gap-px bg-white/5">
        <div className="bg-nexus-panel border-l-2 border-nexus-cyan p-6 lg:col-span-2">
          <div className="label-eyebrow text-nexus-cyan">// nexus revenue · all time</div>
          <div className="font-display font-black text-5xl mt-3 text-nexus-cyan text-glow-cyan">${rev.total.toLocaleString()}</div>
          <div className="text-[11px] text-white/40 mt-2 tracking-wider">today: ${rev.today.toFixed(2)} · 7d: ${rev.week.toFixed(2)} · mrr: ${rev.mrr.toFixed(2)}</div>
        </div>
        <div className="bg-nexus-surface p-6">
          <div className="label-eyebrow">// active studio</div>
          <div className="font-display font-black text-3xl mt-3">{rev.active_studio_members}</div>
          <div className="text-[11px] text-white/40 tracking-wider mt-1">@ ${(rev.studio_price ?? 0).toFixed(2)}/mo each</div>
        </div>
        <div className="bg-nexus-surface p-6">
          <div className="label-eyebrow">// gross volume tracked</div>
          <div className="font-display font-black text-3xl mt-3">${(rev.gross_volume.tips_all + rev.gross_volume.subs_all + rev.gross_volume.coin_sales_all).toLocaleString()}</div>
          <div className="text-[11px] text-white/40 tracking-wider mt-1">tips · subs · coins</div>
        </div>
      </div>

      {/* REVENUE BREAKDOWN */}
      <div>
        <div className="label-eyebrow mb-3">// where nexus earns</div>
        <div className="space-y-px bg-white/5">
          {rev.breakdown.map((r) => (
            <div key={r.source} className="bg-nexus-surface p-5 grid sm:grid-cols-5 gap-4 items-center" data-testid={`rev-${r.source.split(' ')[0].toLowerCase()}`}>
              <div className="sm:col-span-2 flex items-center gap-3">
                <div className="w-1.5 h-10" style={{ background: r.color }} />
                <div>
                  <div className="text-sm font-medium">{r.source}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-0.5">live · auto-tracked</div>
                </div>
              </div>
              <div><span className="label-eyebrow">today</span><div className="font-mono text-sm mt-1">${r.today.toFixed(2)}</div></div>
              <div><span className="label-eyebrow">7-day</span><div className="font-mono text-sm mt-1">${r.week.toFixed(2)}</div></div>
              <div className="text-right"><span className="label-eyebrow">all time</span><div className="font-display font-bold text-xl mt-1" style={{ color: r.color }}>${r.amount.toFixed(2)}</div></div>
            </div>
          ))}
        </div>
      </div>

      {/* LIVE TAKEOVER PREVIEWS — owner can see/jump into every live stream */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">// live streams · click any to watch / raid / hype</div>
          <Link to="/explore" className="text-[10px] tracking-[0.3em] uppercase text-nexus-cyan">All streams →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {liveStreams.length === 0 && <div className="col-span-full text-white/40 text-sm bg-nexus-surface p-6">No streams live right now.</div>}
          {liveStreams.slice(0, 8).map((s) => (
            <Link to={`/watch/${s.id}`} key={s.id} className="relative aspect-video overflow-hidden border border-white/10 group hover:border-nexus-amber transition-colors" data-testid={`live-takeover-${s.id}`}>
              <img src={s.thumbnail} alt="" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all" />
              <div className="absolute inset-0 bg-gradient-to-t from-nexus-obsidian via-transparent to-transparent" />
              <div className="absolute top-2 left-2 bg-nexus-crimson/95 px-1.5 py-0.5 text-[8px] tracking-[0.3em] font-display animate-pulse">● LIVE</div>
              <div className="absolute bottom-2 left-2 right-2">
                <div className="text-[11px] font-medium truncate">{s.title}</div>
                <div className="text-[9px] text-white/60 mt-0.5">{s.creator_name} · {(s.viewers || 0).toLocaleString()}</div>
              </div>
              <div className="absolute top-2 right-2 bg-nexus-amber text-nexus-obsidian px-1.5 py-0.5 text-[8px] tracking-[0.3em] font-display font-black opacity-0 group-hover:opacity-100 transition-opacity">TAKE OVER →</div>
            </Link>
          ))}
        </div>
      </div>

      {/* PLATFORM HEALTH METRICS */}
      <div>
        <div className="label-eyebrow mb-3">// platform health</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-white/5">
          {[
            ["MAU", m.mau.toLocaleString()], ["DAU", m.dau.toLocaleString()],
            ["Live", m.live_streams], ["Creators", m.creators],
            ["Uptime", `${m.uptime}%`], ["Churn", `${m.churn}%`],
            ["Watch Hrs", m.watch_hours.toLocaleString()], ["Streams", m.total_streams],
            ["ARPU", `$${m.arpu}`], ["Incidents", m.incidents],
            ["Users", m.users.toLocaleString()], ["Snapshot", new Date(rev.snapshot_at).toLocaleTimeString()],
          ].map(([l, v]) => (
            <div key={l} className="bg-nexus-surface p-4">
              <div className="label-eyebrow">{l}</div>
              <div className="font-display font-black text-xl mt-1.5">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================== INTEGRATIONS ============================== */
// Live status badge for the LiveKit SFU — powered by /owner/livekit/health.
// This is the single source of truth for "is video calling working right now?"
// Auto-refreshes every 30s and lets the owner force a heal cycle.
function LiveKitHealthCard() {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/owner/livekit/health"); setHealth(data); }
    catch (e) { setHealth({ status: "red", reason: e?.response?.data?.detail || "Failed to fetch health" }); }
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);
  const heal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/owner/livekit/heal");
      setHealth(data);
      toast.success(`Heal complete · ${data.ghost_rooms_cleaned || 0} ghost rooms cleaned`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Heal failed"); }
    setBusy(false);
  };

  const statusColor = { green: "#00FF66", amber: "#FFC107", red: "#FF3366" }[health?.status] || "rgba(255,255,255,0.4)";
  return (
    <div className="glass p-5 border-l-2" style={{ borderLeftColor: statusColor }} data-testid="livekit-health-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow" style={{ color: statusColor }}>// livekit health</div>
          <div className="font-display font-bold text-lg mt-1 flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: statusColor }} />
            {health ? health.status.toUpperCase() : "checking…"}
            {health?.cached && <span className="text-[9px] text-white/40 font-mono">(cached {Math.round(health.age_sec || 0)}s)</span>}
          </div>
        </div>
        <button onClick={heal} disabled={busy} className="btn-ghost" data-testid="livekit-heal-btn">
          {busy ? "healing…" : "Force heal now"}
        </button>
      </div>
      {health && (
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs" data-testid="livekit-health-grid">
          <StatMini label="server" value={health.url ? new URL(health.url.replace("wss://","https://").replace("ws://","http://")).host : "—"} tiny />
          <StatMini label="scheme" value={health.url_scheme_ok ? "wss ✓" : "invalid"} color={health.url_scheme_ok ? "#00FF66" : "#FF3366"} />
          <StatMini label="keys" value={health.keys_configured ? "loaded ✓" : "missing"} color={health.keys_configured ? "#00FF66" : "#FF3366"} />
          <StatMini label="token mint" value={health.token_mint_ok ? "ok ✓" : "fail"} color={health.token_mint_ok ? "#00FF66" : "#FF3366"} />
          <StatMini label="active rooms" value={health.active_rooms ?? "—"} />
          <StatMini label="participants" value={health.active_participants ?? "—"} />
          <StatMini label="ghosts cleaned" value={health.ghost_rooms_cleaned ?? 0} />
          <StatMini label="last check" value={health.ts ? new Date(health.ts).toLocaleTimeString() : "—"} tiny />
        </div>
      )}
      {health?.reason && health.status !== "green" && (
        <div className="mt-3 text-[11px] text-nexus-crimson bg-nexus-crimson/10 border border-nexus-crimson/30 rounded p-2 break-all" data-testid="livekit-health-reason">
          {health.reason}
        </div>
      )}
      <div className="mt-3 text-[10px] text-white/40">
        Auto-refresh: 30s · Backend cron: 60s · Ghost cleanup: every 5 min · Fires alerts when status flips red.
      </div>
    </div>
  );
}

function StatMini({ label, value, color, tiny }) {
  return (
    <div>
      <div className="label-eyebrow text-white/40">{label}</div>
      <div className={`font-display font-bold mt-1 ${tiny ? "text-xs break-all" : "text-lg"}`} style={color ? { color } : {}}>
        {value}
      </div>
    </div>
  );
}

/* ============================== INTEGRATIONS ============================== */
function Integrations() {
  const [ints, setInts] = useState([]);
  const [form, setForm] = useState({ provider: "livekit", api_key: "", meta: "" });
  const [newSlot, setNewSlot] = useState({ provider: "", label: "" });
  const load = () => api.get("/owner/integrations").then(({ data }) => setInts(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.api_key) { toast.error("Key required"); return; }
    await api.post("/owner/integrations", { provider: form.provider, api_key: form.api_key, meta: form.meta ? { note: form.meta } : {} });
    toast.success(`${form.provider} configured`);
    setForm({ ...form, api_key: "", meta: "" });
    load();
  };
  const reset = async (p) => { await api.delete(`/owner/integrations/${p}`); toast.success(`${p} reset`); load(); };
  const addSlot = async () => {
    if (!newSlot.provider || !newSlot.label) { toast.error("Both fields needed"); return; }
    try { await api.post("/owner/integrations/add_slot", newSlot); toast.success("Slot added"); setNewSlot({ provider: "", label: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="integrations-tab">
      <LiveKitHealthCard />
      <p className="text-xs text-white/60">Plug in LiveKit (live video), PayPal (payouts), Stripe (payments), SendGrid (email), Twilio (SMS), Cloudflare Stream (CDN). Keys stay server-side.</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
        {ints.map((i) => (
          <div key={i.provider} className="bg-nexus-surface p-5">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">{i.label}</div>
              <div className={`text-[10px] tracking-[0.2em] uppercase ${i.configured ? "text-nexus-mint" : "text-white/30"}`}>{i.configured ? "● configured" : "○ empty"}</div>
            </div>
            <div className="text-[10px] mt-2 tracking-[0.2em] uppercase text-white/40">{i.provider}</div>
            {i.configured && <button onClick={() => reset(i.provider)} className="text-[10px] text-nexus-crimson mt-3 hover:underline">Reset key</button>}
          </div>
        ))}
      </div>

      <div className="glass p-5 grid sm:grid-cols-4 gap-3 items-end">
        <div>
          <div className="label-eyebrow mb-2">provider</div>
          <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm" data-testid="int-provider">
            {ints.map((i) => <option key={i.provider} value={i.provider}>{i.label}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <div className="label-eyebrow mb-2">api key / secret</div>
          <input value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="sk-..." className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono" data-testid="int-key" />
        </div>
        <button onClick={save} className="btn-nexus" data-testid="int-save">Save</button>
      </div>

      <div className="glass p-5">
        <div className="label-eyebrow mb-3">// add new integration slot</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <input placeholder="provider key (e.g. openai)" value={newSlot.provider} onChange={(e) => setNewSlot({ ...newSlot, provider: e.target.value })} className="bg-transparent border border-white/10 px-3 py-2 text-sm" />
          <input placeholder="display label" value={newSlot.label} onChange={(e) => setNewSlot({ ...newSlot, label: e.target.value })} className="bg-transparent border border-white/10 px-3 py-2 text-sm" />
          <button onClick={addSlot} className="btn-ghost">Add Slot</button>
        </div>
      </div>
    </div>
  );
}

/* ============================== STREAMS ============================== */
function Streams() {
  const [streams, setStreams] = useState([]);
  const load = () => api.get("/streams").then(({ data }) => setStreams(data));
  useEffect(() => { load(); }, []);
  const shutdown = async (id) => { await api.post(`/owner/streams/${id}/shutdown`); toast.success("Stream shut down"); load(); };
  const boost = async (id) => { await api.post(`/owner/streams/${id}/boost?viewers=1000`); toast.success("+1000 viewers boosted"); load(); };
  return (
    <div className="space-y-px bg-white/5" data-testid="streams-tab">
      {streams.map((s) => (
        <div key={s.id} className="bg-nexus-surface p-4 flex items-center gap-4">
          <img src={s.thumbnail} alt="" className="w-24 aspect-video object-cover" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{s.title}</div>
            <div className="text-[11px] text-white/40 mt-1">{s.creator_name} · {s.viewers.toLocaleString()} viewers</div>
          </div>
          {s.is_live ? <div className="bg-nexus-crimson px-2 py-1 text-[9px] tracking-[0.3em] font-display uppercase">Live</div> : <div className="text-[10px] tracking-[0.3em] uppercase text-white/30">Offline</div>}
          <button onClick={() => boost(s.id)} className="btn-ghost !py-2 !px-3 text-[9px]" data-testid={`boost-${s.id}`}><Zap className="w-3 h-3" /> Boost</button>
          {s.is_live && <button onClick={() => shutdown(s.id)} className="btn-ghost !py-2 !px-3 text-[9px] !border-nexus-crimson !text-nexus-crimson" data-testid={`shutdown-${s.id}`}><Power className="w-3 h-3" /> Shutdown</button>}
        </div>
      ))}
    </div>
  );
}

/* ============================== STICKERS ============================== */
function Stickers() {
  const [stickers, setStickers] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/stickers").then(({ data }) => setStickers(data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const body = { name: editing.name, emoji: editing.emoji, price: parseFloat(editing.price), tier: editing.tier, animation: editing.animation, color: editing.color };
    if (editing.id) await api.patch(`/owner/stickers/${editing.id}`, body);
    else await api.post("/owner/stickers", body);
    toast.success("Sticker saved"); setEditing(null); load();
  };
  const del = async (id) => { await api.delete(`/owner/stickers/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="space-y-4" data-testid="stickers-tab">
      <button onClick={() => setEditing({ name: "", emoji: "✨", price: 5, tier: "paid", animation: "float", color: "#00F0FF" })} className="btn-nexus" data-testid="add-sticker"><Plus className="w-3.5 h-3.5" /> New Sticker</button>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-white/5">
        {stickers.map((s) => (
          <div key={s.id} className="bg-nexus-surface p-5 text-center">
            <div className="text-5xl" style={{ filter: `drop-shadow(0 0 12px ${s.color})` }}>{s.emoji}</div>
            <div className="text-sm font-medium mt-3">{s.name}</div>
            <div className="text-[10px] tracking-[0.3em] uppercase mt-1" style={{ color: s.color }}>{s.tier} · {s.price > 0 ? `${s.price}c` : "free"}</div>
            <div className="flex justify-center gap-2 mt-3">
              <button onClick={() => setEditing(s)} className="text-white/40 hover:text-nexus-cyan"><Edit3 className="w-3.5 h-3.5" /></button>
              <button onClick={() => del(s.id)} className="text-white/40 hover:text-nexus-crimson"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit sticker" : "New sticker"}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
            <Field label="emoji" value={editing.emoji} onChange={(v) => setEditing({ ...editing, emoji: v })} />
            <Field label="price (NX)" value={editing.price} type="number" onChange={(v) => setEditing({ ...editing, price: v })} />
            <div>
              <div className="label-eyebrow mb-2">tier</div>
              <select value={editing.tier} onChange={(e) => setEditing({ ...editing, tier: e.target.value })} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm">
                <option value="free">free</option><option value="paid">paid</option><option value="premium">premium</option><option value="elite">elite</option>
              </select>
            </div>
            <div>
              <div className="label-eyebrow mb-2">animation</div>
              <select value={editing.animation} onChange={(e) => setEditing({ ...editing, animation: e.target.value })} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm">
                <option value="float">float</option><option value="pulse">pulse</option><option value="zap">zap</option><option value="spin">spin</option><option value="launch">launch</option><option value="burn">burn</option><option value="warp">warp</option><option value="shimmer">shimmer</option>
              </select>
            </div>
            <Field label="color (hex)" value={editing.color} onChange={(v) => setEditing({ ...editing, color: v })} />
          </div>
          <button onClick={save} className="btn-nexus w-full" data-testid="save-sticker">Save sticker</button>
        </Modal>
      )}
    </div>
  );
}

/* ============================== PRICING ============================== */
function Pricing() {
  const [p, setP] = useState({});
  const load = () => api.get("/owner/pricing").then(({ data }) => setP(data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const body = {
      withdrawal_fee_pct: parseFloat(p.withdrawal_fee_pct ?? 0.03),
      withdrawal_fee_min: parseFloat(p.withdrawal_fee_min ?? 0.5),
    };
    await api.post("/owner/pricing", body);
    toast.success("Pricing updated"); load();
  };
  return (
    <div className="space-y-6" data-testid="pricing-tab">
      <p className="text-xs text-white/60">Adjust withdrawal fees. Changes apply immediately.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Field label="withdrawal fee % (0.03 = 3%)" value={p.withdrawal_fee_pct ?? 0.03} type="number" onChange={(v) => setP({ ...p, withdrawal_fee_pct: v })} />
        <Field label="withdrawal fee min ($)" value={p.withdrawal_fee_min ?? 0.5} type="number" onChange={(v) => setP({ ...p, withdrawal_fee_min: v })} />
      </div>
      <button onClick={save} className="btn-nexus" data-testid="save-pricing">Save pricing</button>
    </div>
  );
}

/* ============================== USERS · extracted to ./owner/UserAdminTabs.jsx ============================== */
/* UsersTab, SponsorPoolTab — imported above. Definitions moved out to keep this file slim. */

/* ============================== FLAGS ============================== */
function Flags() {
  const [flags, setFlags] = useState([]);
  const load = () => api.get("/owner/flags").then(({ data }) => setFlags(data));
  useEffect(() => { load(); }, []);
  const toggle = async (f) => { await api.post("/owner/flags", { key: f.key, enabled: !f.enabled }); toast.success(`${f.label} ${!f.enabled ? "ON" : "OFF"}`); load(); };
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5" data-testid="flags-tab">
      {flags.map((f) => (
        <button key={f.key} onClick={() => toggle(f)} data-testid={`flag-${f.key}`} className="bg-nexus-surface p-5 text-left hover:bg-nexus-panel transition">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">{f.label}</div>
            <div className={`w-10 h-5 rounded-full relative transition ${f.enabled ? "bg-nexus-cyan" : "bg-white/10"}`}>
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${f.enabled ? "left-5" : "left-0.5"}`} />
            </div>
          </div>
          <div className="text-[10px] mt-2 tracking-[0.2em] uppercase text-white/40">{f.key}</div>
        </button>
      ))}
    </div>
  );
}

/* ============================== TAKEOVERS ============================== */
function Takeovers() {
  const [streams, setStreams] = useState([]);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ stream_id: "", message: "", type: "spotlight" });
  const load = () => { api.get("/streams").then(({ data }) => setStreams(data)); api.get("/owner/takeovers").then(({ data }) => setList(data)); };
  useEffect(() => { load(); }, []);
  const fire = async () => {
    if (!form.stream_id) { toast.error("Select a stream"); return; }
    await api.post("/owner/takeover", form);
    toast.success(`${form.type} fired`);
    setForm({ stream_id: "", message: "", type: "spotlight" });
    load();
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="takeovers-tab">
      <div className="lg:col-span-2 glass p-5 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <div className="label-eyebrow mb-2">type</div>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm" data-testid="takeover-type">
              <option value="spotlight">Spotlight Boost (+5k viewers)</option>
              <option value="raid">Raid Stream</option>
              <option value="giftdrop">Gift Drop</option>
              <option value="announce">Platform Announce</option>
            </select>
          </div>
          <div>
            <div className="label-eyebrow mb-2">target stream</div>
            <select value={form.stream_id} onChange={(e) => setForm({ ...form, stream_id: e.target.value })} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm" data-testid="takeover-stream">
              <option value="">— select —</option>
              {streams.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </div>
        </div>
        <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Hype message…" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm h-24" data-testid="takeover-msg" />
        <button onClick={fire} className="btn-nexus" data-testid="takeover-fire"><Sparkles className="w-3.5 h-3.5" /> Fire</button>
      </div>
      <div className="glass p-5">
        <div className="label-eyebrow mb-3">// recent</div>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {list.length === 0 && <div className="text-xs text-white/30">None yet.</div>}
          {list.map((t) => (
            <div key={t.id} className="text-xs border-l-2 border-nexus-amber pl-3 py-1">
              <div className="font-display tracking-wider uppercase text-nexus-amber text-[10px]">{t.type}</div>
              <div className="text-white/70">{t.message}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================== SPONSORED TAKEOVERS ============================== */
function SponsoredTakeovers() {
  const [list, setList] = useState([]);
  const [creators, setCreators] = useState([]);
  const [form, setForm] = useState({ brand_name: "", brand_color: "#FF007F", duration_hours: 24, featured_creator_id: "", sponsor_fee: 5000 });
  const load = () => {
    api.get("/owner/sponsored_takeovers").then(({ data }) => setList(data));
    api.get("/owner/users").then(({ data }) => setCreators(data.filter((u) => u.role === "creator")));
  };
  useEffect(() => { load(); }, []);
  const fire = async () => {
    if (!form.brand_name) { toast.error("Brand name required"); return; }
    await api.post("/owner/sponsored_takeovers", { ...form, sponsor_fee: parseFloat(form.sponsor_fee), duration_hours: parseInt(form.duration_hours) });
    toast.success(`${form.brand_name} takeover live`);
    setForm({ ...form, brand_name: "" });
    load();
  };
  const end = async (id) => { await api.post(`/owner/sponsored_takeovers/${id}/end`); toast.success("Sponsorship ended"); load(); };
  return (
    <div className="space-y-6" data-testid="sponsored-tab">
      <p className="text-xs text-white/60">Sell a 24-hour brand takeover. 30% of viewer tips during the window route to the featured creator. Revenue from sponsors + creator hype boost.</p>
      <div className="glass p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="brand name" value={form.brand_name} onChange={(v) => setForm({ ...form, brand_name: v })} />
        <Field label="brand color" value={form.brand_color} onChange={(v) => setForm({ ...form, brand_color: v })} />
        <Field label="duration (hours)" type="number" value={form.duration_hours} onChange={(v) => setForm({ ...form, duration_hours: v })} />
        <Field label="sponsor fee ($)" type="number" value={form.sponsor_fee} onChange={(v) => setForm({ ...form, sponsor_fee: v })} />
        <div>
          <div className="label-eyebrow mb-2">featured creator (optional)</div>
          <select value={form.featured_creator_id} onChange={(e) => setForm({ ...form, featured_creator_id: e.target.value })} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm">
            <option value="">— none —</option>
            {creators.map((c) => <option key={c.id} value={c.id}>{c.display_name}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <button onClick={fire} className="btn-nexus" data-testid="sp-fire"><Trophy className="w-3.5 h-3.5" /> Sell Takeover</button>
        </div>
      </div>
      <div>
        <div className="label-eyebrow mb-3">// active & past sponsorships</div>
        <div className="space-y-px bg-white/5">
          {list.length === 0 && <div className="bg-nexus-surface p-6 text-white/40 text-sm">No sponsorships yet.</div>}
          {list.map((t) => (
            <div key={t.id} className="bg-nexus-surface p-4 flex items-center gap-4 flex-wrap">
              <div className="w-3 h-12" style={{ background: t.brand_color }} />
              <div className="flex-1">
                <div className="text-sm font-medium">{t.brand_name}</div>
                <div className="text-[11px] text-white/40">${t.sponsor_fee} · {t.duration_hours}h · {t.active ? "active" : "ended"}</div>
              </div>
              {t.active && <button onClick={() => end(t.id)} className="btn-ghost !py-1.5 !px-3 text-[9px]">End</button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================== CONTENT (BREELS / MERCH) ============================== */
function Content() {
  const [breels, setBreels] = useState([]);
  const [merch, setMerch] = useState([]);
  const [bForm, setBForm] = useState({ title: "", creator: "", duration: 60, thumbnail: "", views: 0 });
  const [mForm, setMForm] = useState({ name: "", price: 0, image: "", creator: "ELYSIUM" });
  const load = () => { api.get("/breels").then(({ data }) => setBreels(data)); api.get("/merch").then(({ data }) => setMerch(data)); };
  useEffect(() => { load(); }, []);
  const addBreel = async () => { await api.post("/owner/breels", { ...bForm, duration: parseInt(bForm.duration), views: parseInt(bForm.views) }); toast.success("BReel added"); setBForm({ title: "", creator: "", duration: 60, thumbnail: "", views: 0 }); load(); };
  const delBreel = async (id) => { await api.delete(`/owner/breels/${id}`); load(); };
  const addMerch = async () => { await api.post("/owner/merch", { ...mForm, price: parseFloat(mForm.price) }); toast.success("Merch added"); setMForm({ name: "", price: 0, image: "", creator: "ELYSIUM" }); load(); };
  const delMerch = async (id) => { await api.delete(`/owner/merch/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="content-tab">
      <div className="space-y-3">
        <div className="label-eyebrow">// breels (ai short films)</div>
        <div className="glass p-4 space-y-2">
          <Field label="title" value={bForm.title} onChange={(v) => setBForm({ ...bForm, title: v })} />
          <Field label="creator" value={bForm.creator} onChange={(v) => setBForm({ ...bForm, creator: v })} />
          <Field label="thumbnail url" value={bForm.thumbnail} onChange={(v) => setBForm({ ...bForm, thumbnail: v })} />
          <div className="grid grid-cols-2 gap-2">
            <Field label="duration (s)" type="number" value={bForm.duration} onChange={(v) => setBForm({ ...bForm, duration: v })} />
            <Field label="views" type="number" value={bForm.views} onChange={(v) => setBForm({ ...bForm, views: v })} />
          </div>
          <button onClick={addBreel} className="btn-nexus w-full">Add BReel</button>
        </div>
        <div className="space-y-px bg-white/5">
          {breels.map((b) => (
            <div key={b.id} className="bg-nexus-surface p-3 flex items-center gap-3">
              <img src={b.thumbnail} alt="" className="w-12 h-12 object-cover" />
              <div className="flex-1"><div className="text-sm">{b.title}</div><div className="text-[11px] text-white/40">{b.creator} · {b.duration}s · {b.views.toLocaleString()} views</div></div>
              <button onClick={() => delBreel(b.id)} className="text-nexus-crimson hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        <div className="label-eyebrow">// elysium merch</div>
        <div className="glass p-4 space-y-2">
          <Field label="name" value={mForm.name} onChange={(v) => setMForm({ ...mForm, name: v })} />
          <Field label="price ($)" type="number" value={mForm.price} onChange={(v) => setMForm({ ...mForm, price: v })} />
          <Field label="image url" value={mForm.image} onChange={(v) => setMForm({ ...mForm, image: v })} />
          <Field label="creator" value={mForm.creator} onChange={(v) => setMForm({ ...mForm, creator: v })} />
          <button onClick={addMerch} className="btn-nexus w-full">Add Merch</button>
        </div>
        <div className="space-y-px bg-white/5">
          {merch.map((m) => (
            <div key={m.id} className="bg-nexus-surface p-3 flex items-center gap-3">
              <img src={m.image} alt="" className="w-12 h-12 object-cover" />
              <div className="flex-1"><div className="text-sm">{m.name}</div><div className="text-[11px] text-white/40">{m.creator} · ${m.price}</div></div>
              <button onClick={() => delMerch(m.id)} className="text-nexus-crimson hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================== COIN DROPS ============================== */
function CoinDrops() {
  const [drops, setDrops] = useState([]);
  const [form, setForm] = useState({ label: "Flash Drop · 24h", bundle_id: "nx_100_flash", coins: 100, discounted_price: 1.00, duration_hours: 24 });
  const load = () => api.get("/coins/drops").then(({ data }) => setDrops(data));
  useEffect(() => { load(); }, []);
  const fire = async () => {
    if (!form.label) { toast.error("Label required"); return; }
    await api.post("/owner/coin_drops", { ...form, coins: parseInt(form.coins), discounted_price: parseFloat(form.discounted_price), duration_hours: parseInt(form.duration_hours) });
    toast.success("Flash drop launched"); load();
  };
  const endDrop = async (id) => { await api.post(`/owner/coin_drops/${id}/end`); toast.success("Drop ended"); load(); };
  return (
    <div className="space-y-6" data-testid="drops-tab">
      <p className="text-xs text-white/60">Create limited-time discounted NX bundles. They appear on the Quests page as Flash Drops.</p>
      <div className="glass p-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <Field label="label" value={form.label} onChange={(v) => setForm({ ...form, label: v })} />
        <Field label="bundle id" value={form.bundle_id} onChange={(v) => setForm({ ...form, bundle_id: v })} />
        <Field label="coins (NX)" type="number" value={form.coins} onChange={(v) => setForm({ ...form, coins: v })} />
        <Field label="price ($)" type="number" value={form.discounted_price} onChange={(v) => setForm({ ...form, discounted_price: v })} />
        <Field label="hours" type="number" value={form.duration_hours} onChange={(v) => setForm({ ...form, duration_hours: v })} />
        <button onClick={fire} className="btn-nexus sm:col-span-2 lg:col-span-5">Launch Drop</button>
      </div>
      <div className="space-y-px bg-white/5">
        {drops.length === 0 && <div className="bg-nexus-surface p-6 text-white/40 text-sm">No active drops.</div>}
        {drops.map((d) => (
          <div key={d.id} className="bg-nexus-surface p-4 flex items-center gap-4 flex-wrap">
            <div className="flex-1"><div className="text-sm font-medium">{d.label}</div><div className="text-[11px] text-white/40">{d.coins} NX · ${d.discounted_price} · ends {new Date(d.ends_at).toLocaleString()}</div></div>
            <button onClick={() => endDrop(d.id)} className="btn-ghost !py-1.5 !px-3 text-[9px] !border-nexus-crimson !text-nexus-crimson">End</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================== PLATFORM SETTINGS ============================== */
function PlatformSettings() {
  const [s, setS] = useState({});
  const [emailBusy, setEmailBusy] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const load = () => api.get("/owner/settings").then(({ data }) => setS(data));
  useEffect(() => { load(); }, []);
  const save = async (k, v) => { await api.post("/owner/settings", { key: k, value: v }); toast.success(`${k} saved`); load(); };
  const refreshFx = async () => {
    if (refreshBusy) return;
    setRefreshBusy(true);
    try {
      const { data } = await api.post("/owner/fx-rates/refresh");
      toast.success(`Pulled ${data.count} live rates from frankfurter.dev`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    setRefreshBusy(false);
  };
  const sendWeeklyEmail = async () => {
    if (emailBusy) return;
    setEmailBusy(true);
    try {
      await api.post("/owner/fx-rates/send-weekly-email");
      toast.success("Weekly FX summary email sent to the owner inbox");
    } catch (e) { toast.error(e?.response?.data?.detail || "Email send failed"); }
    setEmailBusy(false);
  };
  return (
    <div className="space-y-5" data-testid="settings-tab">
      <p className="text-xs text-white/60">Edit hero copy, tagline, and other platform-wide strings. Changes are reflected publicly.</p>
      {[
        { k: "hero_title", l: "Hero title (use \\n for line breaks)", t: "textarea" },
        { k: "hero_subtitle", l: "Hero subtitle", t: "textarea" },
        { k: "platform_tagline", l: "Platform tagline", t: "text" },
        { k: "studio_signup_bonus_nx", l: "Studio signup bonus NX (0 = no bonus, owner-controlled)", t: "text" },
        { k: "nx_payout_rate_aud", l: "NX → AUD payout rate", t: "text" },
      ].map((row) => (
        <div key={row.k} className="glass p-4 space-y-2">
          <div className="label-eyebrow">{row.l}</div>
          {row.t === "textarea" ?
            <textarea value={s[row.k] || ""} onChange={(e) => setS({ ...s, [row.k]: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm h-24" data-testid={`set-${row.k}`} /> :
            <input value={s[row.k] || ""} onChange={(e) => setS({ ...s, [row.k]: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid={`set-${row.k}`} />}
          <button onClick={() => save(row.k, s[row.k])} className="btn-ghost !py-1.5 !px-4 text-[9px]" data-testid={`save-${row.k}`}>Save</button>
        </div>
      ))}
      <div className="glass p-4 space-y-3" data-testid="fx-cron-card">
        <div className="flex items-start gap-3">
          <DollarSign className="w-5 h-5 text-nexus-cyan mt-0.5" />
          <div className="flex-1">
            <div className="label-eyebrow">// fx cron · live AUD rates</div>
            <div className="text-sm text-white/70 mt-1">
              Live AUD→foreign rates refresh daily from frankfurter.dev. A weekly summary email fires every Monday morning (UTC) so you can clear stale overrides.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={refreshFx} disabled={refreshBusy} className="btn-ghost !py-1.5 !px-3 text-[10px]" data-testid="fx-refresh-now">
            {refreshBusy ? "Pulling…" : "Refresh rates now"}
          </button>
          <button onClick={sendWeeklyEmail} disabled={emailBusy} className="btn-nexus !py-1.5 !px-3 text-[10px]" data-testid="fx-email-send-now">
            {emailBusy ? "Sending…" : "Send Monday email preview"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ============================== Extracted: OwnerMobileGoLive, GrantPanel, Modal → ./owner/UserAdminTabs.jsx ============================== */

/* ============================== OWNER ED3N PANEL ============================== */
function OwnerEd3nPanel({ onClose }) {
  const [prompt, setPrompt] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    if (!prompt.trim()) return;
    setBusy(true); setReply("");
    try {
      const { data } = await api.post("/eden/ask", { prompt, context: "owner" });
      setReply(data.reply);
    } catch (e) { toast.error(e?.response?.data?.detail || "ED3N error"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={onClose}>
      <div className="glass-strong p-6 max-w-2xl w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="owner-ed3n-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl flex items-center gap-2"><Bot className="w-5 h-5 text-nexus-violet" />ED3N AI · Owner Mode</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-white/50">Knows every NEXUS feature: dashboards, monetisation, KYC, integrations, Streamlabs, BReels, ELYSIUM Wear, raids, hype meters. Ask anything.</p>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. 'Suggest 3 marketing campaigns for the launch'" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm h-24" data-testid="owner-ed3n-prompt" />
        <button onClick={ask} disabled={busy} className="btn-nexus w-full justify-center" data-testid="owner-ed3n-ask">{busy ? "Thinking…" : "Ask ED3N"}</button>
        {reply && <div className="ed3n-output bg-nexus-surface p-4 border border-white/10 text-sm text-white/80" data-testid="owner-ed3n-reply" dangerouslySetInnerHTML={{ __html: renderSafeMarkdown(reply) }} />}
      </div>
    </div>
  );
}

/* ============================== TUTORIALS & FILES ============================== */
function TutorialsTab() {
  const [items, setItems] = useState([]);
  const [docs, setDocs] = useState([]);
  const [audience, setAudience] = useState("all");
  const [view, setView] = useState("tutorials");  // tutorials | documents
  useEffect(() => {
    api.get("/tutorials").then(({ data }) => setItems(data));
    api.get("/documents").then(({ data }) => setDocs(data));
  }, []);
  const audiences = ["all", "viewer", "creator", "studio", "sponsor", "owner"];
  const filtered = audience === "all" ? items : items.filter((t) => t.audience === audience);
  return (
    <div className="space-y-4" data-testid="tutorials-tab">
      <p className="text-xs text-white/60">Step-by-step guides + every document you need to run NEXUS. Tutorials by role · Documents = ad revenue model · KYC · tax · PayPal · LiveKit · legal · merch · BReels · marketing pack.</p>
      <div className="flex gap-2">
        <button onClick={() => setView("tutorials")} className={`btn-ghost text-[10px] ${view === "tutorials" ? "!border-nexus-gold !text-nexus-gold" : ""}`} data-testid="view-tutorials">Tutorials ({items.length})</button>
        <button onClick={() => setView("documents")} className={`btn-ghost text-[10px] ${view === "documents" ? "!border-nexus-gold !text-nexus-gold" : ""}`} data-testid="view-documents">Documents ({docs.length})</button>
      </div>
      {view === "tutorials" && (
        <>
          <div className="flex flex-wrap gap-1">
            {audiences.map((a) => (
              <button key={a} onClick={() => setAudience(a)} className="text-[9px] tracking-[0.3em] uppercase font-display px-3 py-1.5 border" style={{ borderColor: audience === a ? "#FFD700" : "rgba(255,255,255,0.1)", color: audience === a ? "#FFD700" : "rgba(255,255,255,0.5)" }}>{a}</button>
            ))}
          </div>
          <div className="space-y-px bg-white/5">
            {filtered.map((t) => (
              <details key={t.id} className="bg-nexus-surface p-4 group" data-testid={`tutorial-${t.id}`}>
                <summary className="cursor-pointer flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-[9px] tracking-[0.3em] uppercase font-display text-nexus-gold w-16">{t.audience}</div>
                    <div className="text-sm font-medium">{t.title}</div>
                  </div>
                  <div className="text-[10px] text-white/40">{t.minutes} min</div>
                </summary>
                <div className="mt-3 text-xs text-white/70 whitespace-pre-wrap leading-relaxed pl-20 border-l border-white/5">{t.body}</div>
              </details>
            ))}
          </div>
        </>
      )}
      {view === "documents" && (
        <div className="space-y-px bg-white/5">
          {docs.map((d) => (
            <details key={d.id} className="bg-nexus-surface p-4" data-testid={`document-${d.id}`}>
              <summary className="cursor-pointer flex items-center justify-between">
                <div className="text-sm font-medium nexus-shimmer-text">{d.title}</div>
                <button onClick={(e) => { e.preventDefault(); navigator.clipboard?.writeText(`${d.title}\n\n${d.body}`); toast.success("Document copied"); }} className="text-[10px] tracking-wider text-nexus-cyan">COPY →</button>
              </summary>
              <div className="mt-3 text-xs text-white/70 whitespace-pre-wrap leading-relaxed border-l border-white/5 pl-3">{d.body}</div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================== CUSTOMER SERVICE INBOX ============================== */
function SupportTab() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");
  const load = () => api.get("/owner/tickets", { params: filter ? { status: filter } : {} }).then(({ data }) => setTickets(data));
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, [filter]);
  const act = async (action) => {
    try {
      await api.post(`/owner/tickets/${open.id}/action`, { action, reply: reply || undefined });
      toast.success(`Ticket ${action}d`); setOpen(null); setReply(""); load();
    } catch { toast.error("Action failed"); }
  };
  const prioColor = { urgent: "#FF3366", high: "#FFC107", normal: "#00F0FF", low: "rgba(255,255,255,0.4)" };
  return (
    <div className="space-y-4" data-testid="support-tab">
      <p className="text-xs text-white/60">Member tickets via Contact Nexus widget. Auto-triaged by AI keywords (urgent / high / normal / low).</p>
      <div className="flex flex-wrap gap-1">
        {["", "open", "resolved", "escalated"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} className="text-[9px] tracking-[0.3em] uppercase font-display px-3 py-1.5 border" style={{ borderColor: filter === s ? "#00F0FF" : "rgba(255,255,255,0.1)", color: filter === s ? "#00F0FF" : "rgba(255,255,255,0.5)" }}>{s || "all"}</button>
        ))}
      </div>
      <div className="space-y-px bg-white/5">
        {tickets.length === 0 && <div className="bg-nexus-surface p-6 text-white/40 text-sm">No tickets.</div>}
        {tickets.map((t) => (
          <button key={t.id} onClick={() => setOpen(t)} className="bg-nexus-surface p-4 w-full text-left flex items-center gap-3" data-testid={`ticket-${t.id}`}>
            <div className="w-1.5 h-12" style={{ background: prioColor[t.priority] }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{t.subject}</div>
              <div className="text-[10px] text-white/50 mt-0.5">{t.user_name} · {t.category} · {t.priority}</div>
              <div className="text-[11px] text-white/40 mt-1 truncate">{t.body}</div>
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-display" style={{ color: t.status === "open" ? "#FFC107" : t.status === "resolved" ? "#00FF66" : "#FF3366" }}>{t.status}</div>
          </button>
        ))}
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setOpen(null)}>
          <div className="glass-strong p-6 max-w-lg w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="ticket-modal">
            <div className="flex items-center justify-between"><h3 className="font-display text-lg">{open.subject}</h3><button onClick={() => setOpen(null)}><X className="w-4 h-4" /></button></div>
            <div className="text-xs text-white/60 space-y-1">
              <div><strong>From:</strong> {open.user_name} ({open.user_email})</div>
              <div><strong>Priority:</strong> <span style={{ color: prioColor[open.priority] }}>{open.priority}</span> · Status: {open.status}</div>
              <div className="border-l-2 border-white/10 pl-3 mt-2 whitespace-pre-wrap">{open.body}</div>
            </div>
            <textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply (optional)" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm h-24" />
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => act("resolve")} className="btn-ghost text-[10px] !border-nexus-mint !text-nexus-mint" data-testid="ticket-resolve">Resolve</button>
              <button onClick={() => act("escalate")} className="btn-ghost text-[10px] !border-nexus-crimson !text-nexus-crimson">Escalate</button>
              <button onClick={() => act("reply")} className="btn-nexus text-[10px]">Send reply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================== MARKETING ASSETS ============================== */
function MarketingTab() {
  const [items, setItems] = useState([]);
  const [channel, setChannel] = useState("all");
  useEffect(() => { api.get("/marketing/assets").then(({ data }) => setItems(data)); }, []);
  const channels = ["all", ...Array.from(new Set(items.map((i) => i.channel)))];
  const filtered = channel === "all" ? items : items.filter((i) => i.channel === channel);
  return (
    <div className="space-y-4" data-testid="marketing-tab">
      <p className="text-xs text-white/60">50 trial-launch ad templates across X, Instagram, social shorts, LinkedIn. Copy → paste → ship.</p>
      <div className="flex flex-wrap gap-1">
        {channels.map((c) => (
          <button key={c} onClick={() => setChannel(c)} className="text-[9px] tracking-[0.3em] uppercase font-display px-3 py-1.5 border" style={{ borderColor: channel === c ? "#00F0FF" : "rgba(255,255,255,0.1)", color: channel === c ? "#00F0FF" : "rgba(255,255,255,0.5)" }}>{c}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
        {filtered.map((m) => (
          <div key={m.id} className="bg-nexus-surface p-4 space-y-2" data-testid={`asset-${m.id}`}>
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.3em] uppercase font-display text-nexus-amber">{m.channel} · {m.category}</div>
              <button onClick={() => { navigator.clipboard?.writeText(`${m.headline}\n\n${m.body}\n\n${m.cta}`); toast.success("Copied"); }} className="text-[10px] tracking-wider text-nexus-cyan">COPY →</button>
            </div>
            <div className="font-display font-bold text-base">{m.headline}</div>
            <div className="text-xs text-white/60 leading-relaxed">{m.body}</div>
            <div className="text-[10px] text-nexus-mint tracking-wider">{m.cta}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================== OWNER NOTIFICATIONS / EVENT FEED ============================== */
function NotificationsTab() {
  const [n, setN] = useState(null);
  const load = () => api.get("/owner/notifications").then(({ data }) => setN(data));
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);
  if (!n) return <div className="text-white/40">Loading…</div>;
  const t = n.thresholds || {};
  return (
    <div className="space-y-6" data-testid="owner-notifications">
      <p className="text-xs text-white/60">Live event feed — new signups, Studio upgrades, big tips, threshold triggers. Auto-refreshes every 10s.</p>
      <div className="grid sm:grid-cols-5 gap-px bg-white/5">
        <div className="bg-nexus-surface p-4"><div className="label-eyebrow text-nexus-mint">// profit AUD</div><div className="font-display text-2xl mt-1">${t.platform_profit_aud}</div></div>
        <div className="bg-nexus-surface p-4"><div className="label-eyebrow text-nexus-amber">// coin sales AUD</div><div className="font-display text-2xl mt-1">${t.total_coin_sales_aud}</div></div>
        <div className="bg-nexus-surface p-4"><div className="label-eyebrow text-nexus-cyan">// studio subs</div><div className="font-display text-2xl mt-1">{t.active_studio_subs}</div></div>
        <div className="bg-nexus-surface p-4"><div className="label-eyebrow text-nexus-magenta">// signups today</div><div className="font-display text-2xl mt-1">{t.new_users_today}</div></div>
        <div className="bg-nexus-surface p-4"><div className="label-eyebrow text-nexus-violet">// lifetime tips NX</div><div className="font-display text-2xl mt-1">{t.lifetime_tips_nx?.toLocaleString()}</div></div>
      </div>
      <div className="space-y-px bg-white/5">
        {n.events.length === 0 && <div className="bg-nexus-surface p-6 text-white/40 text-sm">No events yet. Activity will stream here in real-time.</div>}
        {n.events.map((e) => (
          <div key={e.id} className="bg-nexus-surface p-3 flex items-center gap-3">
            <div className="text-[10px] tracking-[0.3em] uppercase font-display" style={{ color: e.kind === "big_tip" ? "#FFC107" : e.kind === "studio_upgrade" ? "#00F0FF" : "#FF007F" }}>{e.kind}</div>
            <div className="flex-1 text-xs text-white/80">{e.summary}</div>
            <div className="text-[10px] text-white/30">{new Date(e.ts).toLocaleTimeString()}</div>
          </div>
        ))}
      </div>
      <button onClick={() => api.post("/owner/notifications/clear").then(load)} className="btn-ghost text-[10px]" data-testid="clear-events">Clear feed</button>
    </div>
  );
}

/* ============================== BULK EMAIL / ANNOUNCE ============================== */
function BulkEmailTab() {
  const [form, setForm] = useState({ subject: "", body: "", audience: "all", color: "#00F0FF" });
  const [history, setHistory] = useState([]);
  const load = () => api.get("/owner/announce/history").then(({ data }) => setHistory(data));
  useEffect(() => { load(); }, []);
  const send = async () => {
    if (!form.subject.trim() || !form.body.trim()) { toast.error("Subject + body required"); return; }
    try {
      const { data } = await api.post("/owner/announce", form);
      toast.success(`Delivered to ${data.recipient_count} members`);
      setForm({ ...form, subject: "", body: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };
  return (
    <div className="space-y-4" data-testid="bulk-email">
      <p className="text-xs text-white/60">Send an in-app announcement (also shown in user inbox). Audience targeting included.</p>
      <div className="glass p-5 space-y-3 grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <div className="label-eyebrow mb-1.5">audience</div>
          <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm" data-testid="email-audience">
            <option value="all">All members</option>
            <option value="creators">All creators</option>
            <option value="sponsors">All sponsors</option>
            <option value="studio_tier">Studio tier ($20/mo)</option>
            <option value="viewers">Viewers only</option>
            <option value="flagged">Flagged users only</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <div className="label-eyebrow mb-1.5">subject</div>
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="email-subject" />
        </div>
        <div className="sm:col-span-2">
          <div className="label-eyebrow mb-1.5">body</div>
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm h-32" data-testid="email-body" />
        </div>
        <div className="sm:col-span-2">
          <button onClick={send} className="btn-nexus" data-testid="email-send"><Mail className="w-3.5 h-3.5" /> Send broadcast</button>
        </div>
      </div>
      <div>
        <div className="label-eyebrow mb-3">// history ({history.length})</div>
        <div className="space-y-px bg-white/5">
          {history.length === 0 && <div className="bg-nexus-surface p-4 text-white/40 text-xs">No broadcasts yet.</div>}
          {history.map((h) => (
            <div key={h.id} className="bg-nexus-surface p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium">{h.subject}</div>
                <div className="text-[10px] text-white/40 truncate">{h.body}</div>
              </div>
              <div className="text-[10px] text-white/40">{h.recipient_count} recipients</div>
              <div className="text-[10px] text-white/30">{new Date(h.ts).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================== FLAGGED USERS ============================== */
function FlaggedUsersTab() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/owner/users/flagged").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const moderate = async (uid, action, reason) => {
    try { await api.post("/owner/users/moderate", { user_id: uid, action, reason }); toast.success(`${action} applied`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const flagBg = { critical: "#FF3366", high: "#FFC107", medium: "#00F0FF", low: "rgba(255,255,255,0.3)" };
  return (
    <div className="space-y-4" data-testid="flagged-users">
      <p className="text-xs text-white/60">Members flagged as high-risk by Staff or auto-fraud. Click to view, message, suspend or block.</p>
      <div className="space-y-px bg-white/5">
        {list.length === 0 && <div className="bg-nexus-surface p-6 text-white/40 text-sm">No flagged members. All calm.</div>}
        {list.map((u) => (
          <div key={u.id} className="bg-nexus-surface p-4 flex items-center gap-3" data-testid={`flagged-${u.id}`}>
            <div className="w-2 h-12" style={{ background: flagBg[u.risk_severity || "low"] }} />
            <img src={u.avatar} alt="" className="w-10 h-10" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{u.display_name} · <span className="text-[10px] text-white/40 uppercase tracking-wider">{u.role}</span></div>
              <div className="text-[11px] text-white/50 mt-0.5">{u.risk_reason}</div>
              <div className="text-[9px] tracking-[0.3em] uppercase mt-1" style={{ color: flagBg[u.risk_severity || "low"] }}>{u.risk_severity || "low"} risk{u.suspended ? " · SUSPENDED" : ""}{u.blocked ? " · BLOCKED" : ""}</div>
            </div>
            <button onClick={() => setOpen(u)} className="btn-ghost text-[10px]">View →</button>
          </div>
        ))}
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setOpen(null)}>
          <div className="glass-strong p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="flagged-modal">
            <div className="flex items-center justify-between"><h3 className="font-display text-lg">{open.display_name}</h3><button onClick={() => setOpen(null)}><X className="w-4 h-4" /></button></div>
            <div className="text-xs text-white/60 space-y-1.5">
              <div><strong>Email:</strong> {open.email}</div>
              <div><strong>Role:</strong> {open.role}</div>
              <div><strong>Severity:</strong> <span style={{ color: flagBg[open.risk_severity || "low"] }}>{open.risk_severity}</span></div>
              <div><strong>Reason:</strong> {open.risk_reason}</div>
              <div><strong>Flagged at:</strong> {open.risk_flagged_at && new Date(open.risk_flagged_at).toLocaleString()}</div>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/10">
              <button onClick={() => moderate(open.id, "suspend")} className="btn-ghost text-[10px] !border-nexus-amber !text-nexus-amber" data-testid={`suspend-${open.id}`}>Suspend</button>
              <button onClick={() => moderate(open.id, "unsuspend")} className="btn-ghost text-[10px]">Lift suspension</button>
              <button onClick={() => moderate(open.id, "block")} className="btn-ghost text-[10px] !border-nexus-crimson !text-nexus-crimson" data-testid={`block-${open.id}`}>Block</button>
              <button onClick={() => moderate(open.id, "unblock")} className="btn-ghost text-[10px]">Unblock</button>
              <a href={`/u/${open.id}`} target="_blank" rel="noopener noreferrer" className="btn-ghost text-[10px] col-span-2 justify-center">Open profile →</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OwnerRaid() {
  const [streams, setStreams] = useState([]);
  const [target, setTarget] = useState("");
  const [source, setSource] = useState("");
  const [msg, setMsg] = useState("Owner raid — drop in!");
  useEffect(() => { api.get("/streams").then(({ data }) => setStreams(data.filter((s) => s.is_live))); }, []);
  const fire = async () => {
    if (!target) { toast.error("Pick a target stream"); return; }
    try {
      await api.post("/owner/raid", { target_stream_id: target, source_stream_id: source || null, message: msg });
      toast.success("Raid fired · viewers redirecting within 12s");
    } catch (e) { toast.error(e?.response?.data?.detail || "Raid failed"); }
  };
  return (
    <div className="space-y-4" data-testid="owner-raid-tab">
      <p className="text-xs text-white/60">Pick a live stream as your raid target. Within 12s, viewers across NEXUS (or only viewers of the source stream you select) get redirected to it. +3000 viewers hype-boost applied.</p>
      <div className="glass p-5 space-y-3 grid sm:grid-cols-2 gap-3">
        <div>
          <div className="label-eyebrow mb-2">target stream</div>
          <select value={target} onChange={(e) => setTarget(e.target.value)} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm" data-testid="raid-target">
            <option value="">— select —</option>
            {streams.map((s) => <option key={s.id} value={s.id}>{s.title} · {s.creator_name}</option>)}
          </select>
        </div>
        <div>
          <div className="label-eyebrow mb-2">source stream (optional — leave blank = all streams)</div>
          <select value={source} onChange={(e) => setSource(e.target.value)} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm">
            <option value="">All streams (platform-wide)</option>
            {streams.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <div className="label-eyebrow mb-2">raid message</div>
          <textarea value={msg} onChange={(e) => setMsg(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm h-20" />
        </div>
        <div className="sm:col-span-2">
          <button onClick={fire} className="btn-nexus" data-testid="raid-fire"><Radio className="w-3.5 h-3.5" /> Fire raid</button>
        </div>
      </div>
    </div>
  );
}

/* ============================== TECH CONSOLE ============================== */
function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (next1.length < 8) return toast.error("New password must be at least 8 characters");
    if (next1 !== next2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next1 });
      toast.success("Password changed. Stay signed in or log out from the top-right menu.");
      setCur(""); setNext1(""); setNext2("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="border border-nexus-cyan/40 bg-nexus-cyan/5 p-5 space-y-3" data-testid="change-password-card">
      <div>
        <div className="label-eyebrow text-nexus-cyan flex items-center gap-2"><Shield className="w-3 h-3" />// owner login</div>
        <h4 className="font-display text-base mt-1">Change your password</h4>
        <p className="text-[11px] text-white/55 leading-relaxed mt-1">
          Default is <span className="font-mono text-white">nexus2026</span>. Change this to a production secret <span className="text-white">before</span> deploying. Minimum 8 characters.
        </p>
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        <input type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} className="bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="cp-current" />
        <input type="password" placeholder="New password (8+ chars)" value={next1} onChange={(e) => setNext1(e.target.value)} className="bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="cp-new" />
        <input type="password" placeholder="Confirm new password" value={next2} onChange={(e) => setNext2(e.target.value)} className="bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="cp-confirm" />
      </div>
      <button onClick={submit} disabled={busy || !cur || !next1 || !next2} className="btn-nexus !border-nexus-cyan disabled:opacity-40" data-testid="cp-submit">
        {busy ? "Changing…" : "Change password"}
      </button>
    </div>
  );
}

function ResetSponsoredCard() {
  const [pwd, setPwd] = useState("nexus2026");
  const [clear, setClear] = useState(true);
  const [busy, setBusy] = useState(false);
  const fire = async () => {
    if (pwd.length < 8) return toast.error("Password must be 8+ characters");
    setBusy(true);
    try {
      await api.post("/owner/reset-sponsored-password", { new_password: pwd, clear_gate: clear });
      toast.success(`STR8K1LL3RS password set to "${pwd}". Sign in at /auth.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-nexus-gold/40 bg-nexus-gold/5 p-5 space-y-3" data-testid="reset-sponsored-card">
      <div>
        <div className="label-eyebrow text-nexus-gold flex items-center gap-2"><Sparkles className="w-3 h-3" />// sponsored seed login</div>
        <h4 className="font-display text-base mt-1">Reset STR8K1LL3RS password</h4>
        <p className="text-[11px] text-white/55 leading-relaxed mt-1">
          The sponsored creator seed account is <span className="font-mono text-white">str8k1ll3rs@nexus.io</span>. Set any password you'd like — 8+ characters. Tick "clear first-login gate" so you can log straight in without the forced-change modal.
        </p>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <input
          type="text"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="New password (8+ chars)"
          className="flex-1 min-w-[220px] bg-transparent border border-white/10 px-3 py-2 text-sm font-mono"
          data-testid="rs-password"
        />
        <label className="flex items-center gap-2 text-[11px] text-white/70 cursor-pointer">
          <input type="checkbox" checked={clear} onChange={(e) => setClear(e.target.checked)} data-testid="rs-clear-gate" />
          Clear first-login gate
        </label>
        <button onClick={fire} disabled={busy} className="btn-nexus !border-nexus-gold disabled:opacity-40" data-testid="rs-submit">
          {busy ? "Setting…" : "Reset & set"}
        </button>
      </div>
    </div>
  );
}

function RepairDuplicateIdsCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fire = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/owner/repair-duplicate-user-ids");
      setResult(data);
      const n = data.duplicates_found || 0;
      if (n === 0) toast.success("No duplicates found — DB is clean. Unique index installed.");
      else toast.success(`Repaired ${n} duplicate ID group(s). Affected users must sign in again.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="border border-nexus-magenta/40 bg-nexus-magenta/5 p-5 space-y-3" data-testid="repair-duplicate-ids-card">
      <div>
        <div className="label-eyebrow text-nexus-magenta flex items-center gap-2"><Shield className="w-3 h-3" />// user-id integrity</div>
        <h4 className="font-display text-base mt-1">Repair duplicate user IDs</h4>
        <p className="text-[11px] text-white/55 leading-relaxed mt-1">
          Fixes the bug where two user rows share the same <code className="text-nexus-magenta">id</code>, causing sign-ins
          (e.g. JED) to render another user&apos;s profile (e.g. STR8K1LL3RS) on /studio, /business and /u/&#123;id&#125;.
          Keeps the oldest row, reassigns fresh UUIDs to newer duplicates, then installs a unique index so it can never happen again.
          Affected users need to sign in again to get a fresh JWT.
        </p>
      </div>
      <button onClick={fire} disabled={busy} className="btn-nexus !border-nexus-magenta disabled:opacity-40" data-testid="repair-duplicate-ids-btn">
        {busy ? "Repairing…" : "Repair now"}
      </button>
      {result && (
        <pre className="text-[10px] bg-black/30 border border-white/10 p-3 overflow-x-auto text-white/80 max-h-40" data-testid="repair-result">
{JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}

function ResetPlatformCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fire = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/owner/reset-platform");
      setResult(data);
      toast.success("Platform reset complete — site is launch-ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border border-nexus-crimson/40 bg-nexus-crimson/5 p-5 space-y-3" data-testid="reset-platform-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-eyebrow text-nexus-crimson flex items-center gap-2"><AlertTriangle className="w-3 h-3" />// production reset</div>
          <h4 className="font-display text-base mt-1">Wipe demo content. Zero all stats. Ship clean.</h4>
          <p className="text-[11px] text-white/55 leading-relaxed mt-1">
            Deletes all streams, breels, posts, campaigns, tips, subscriptions, takeovers, withdrawals,
            ad impressions, bounty progress, notifications, watch history, follows, DMs and the structured
            subscribe log. Removes the 6 demo accounts (creator/viewer/sponsor/staff/parent/kid). Zeroes wallet,
            xp, vp on every surviving user (you included). Retains: <span className="text-white">your owner account</span>,
            <span className="text-white"> STR8K1LL3RS sponsored seed</span>, platform settings, stickers, game categories.
          </p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="btn-ghost !border-nexus-crimson !text-nexus-crimson" disabled={busy} data-testid="reset-platform-btn">
              {busy ? "Resetting…" : "Reset platform"}
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="reset-platform-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Reset entire platform?</AlertDialogTitle>
              <AlertDialogDescription>
                This is irreversible. All runtime data — streams, posts, campaigns, tips, subscriptions, logs —
                will be deleted. Wallet balances will be zeroed for every user including you. Designed to be run
                once before launch. Are you absolutely sure?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="reset-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={fire} data-testid="reset-confirm">Yes, reset now</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {result && (
        <div className="border-t border-nexus-crimson/20 pt-3 mt-3 text-[11px] space-y-1" data-testid="reset-result">
          <div className="text-nexus-mint font-display">✓ {result.message}</div>
          <details className="text-white/50">
            <summary className="cursor-pointer">Wiped counts</summary>
            <div className="font-mono mt-2 grid grid-cols-2 gap-x-4 text-[10px]">
              {Object.entries(result.wiped || {}).map(([k, v]) => (
                <div key={k}><span className="text-white/40">{k}:</span> {String(v)}</div>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}

function TechConsole() {
  const [h, setH] = useState(null);
  const load = () => api.get("/owner/system_health").then(({ data }) => setH(data));
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, []);
  if (!h) return <div className="text-white/40">Probing system…</div>;
  const action = async (a) => {
    try { await api.post("/owner/system_action", { action: a }); toast.success(`Action: ${a}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  return (
    <div className="space-y-6" data-testid="tech-tab">
      <p className="text-xs text-white/60">Live system health · DB stats · integration status · diagnostic actions. Refreshes every 15s.</p>
      <ChangePasswordCard />
      <ResetSponsoredCard />
      <RepairDuplicateIdsCard />
      <ResetPlatformCard />
      <div className="grid sm:grid-cols-3 gap-px bg-white/5">
        <div className="bg-nexus-surface p-5">
          <div className="label-eyebrow text-nexus-cyan">// LiveKit</div>
          <div className={`font-display text-2xl mt-2 ${h.integrations.livekit.configured ? "text-nexus-mint" : "text-nexus-crimson"}`}>{h.integrations.livekit.configured ? "● Online" : "○ Not Configured"}</div>
          <div className="text-[10px] text-white/40 mt-1 font-mono break-all">{h.integrations.livekit.url || "no URL"}</div>
        </div>
        <div className="bg-nexus-surface p-5">
          <div className="label-eyebrow text-nexus-amber">// PayPal</div>
          <div className={`font-display text-2xl mt-2 ${h.integrations.paypal.configured ? "text-nexus-mint" : "text-nexus-amber"}`}>{h.integrations.paypal.configured ? "● Live" : "○ PayPal.Me fallback"}</div>
          <div className="text-[10px] text-white/40 mt-1">mode: {h.integrations.paypal.mode}</div>
        </div>
        <div className="bg-nexus-surface p-5">
          <div className="label-eyebrow text-nexus-violet">// ED3N / AI</div>
          <div className={`font-display text-2xl mt-2 ${h.integrations.emergent_llm.configured ? "text-nexus-mint" : "text-nexus-crimson"}`}>{h.integrations.emergent_llm.configured ? "● Online" : "○ Down"}</div>
          <div className="text-[10px] text-white/40 mt-1">Emergent Universal Key</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
        <div className="bg-nexus-surface p-5">
          <div className="label-eyebrow">// db</div>
          <div className="font-display text-lg mt-2">{h.db.data_size_mb} MB</div>
          <div className="text-[10px] text-white/40">{h.db.collections} collections · {h.db.storage_size_mb} MB on disk</div>
        </div>
        {Object.entries(h.counts).map(([k, v]) => (
          <div key={k} className="bg-nexus-surface p-4">
            <div className="label-eyebrow">{k}</div>
            <div className="font-display text-xl mt-1">{v}</div>
          </div>
        ))}
      </div>

      <div className="glass p-5 space-y-3">
        <div className="label-eyebrow">// diagnostic actions</div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => action("clear_cache")} className="btn-ghost text-[10px]" data-testid="action-clear-cache"><Trash2 className="w-3 h-3" /> Clear notifications + raids cache</button>
          <button onClick={load} className="btn-ghost text-[10px]" data-testid="action-refresh"><Activity className="w-3 h-3" /> Refresh now</button>
        </div>
        <div className="text-[10px] text-white/40 leading-relaxed mt-3 border-t border-white/10 pt-3 font-mono">
          {h.hint}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <div className="label-eyebrow mb-2">{label}</div>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" />
    </div>
  );
}


/* ============================== HEALTH CHECK — daily endpoint sweep + Resend email ============================== */
function HealthCheckTab() {
  const [health, setHealth] = useState(null);
  const [bounty, setBounty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [contactEmail, setContactEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const [{ data: h }, { data: b }, { data: c }] = await Promise.all([
        api.get("/owner/health"),
        api.get("/owner/bounty-stats").catch(() => ({ data: null })),
        api.get("/me/contact-email").catch(() => ({ data: { contact_email: "" } })),
      ]);
      setHealth(h); setBounty(b); setContactEmail(c?.contact_email || "");
    } catch (e) { toast.error("Health check failed"); }
    setLoading(false);
  };

  const saveContactEmail = async () => {
    if (!contactEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    setSavingEmail(true);
    try {
      await api.post("/me/contact-email", { contact_email: contactEmail });
      toast.success("Contact email saved");
    } catch { toast.error("Save failed"); }
    setSavingEmail(false);
  };

  const emailNow = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/owner/health/email");
      if (data.sent) toast.success(`✓ Sent to ${data.to}`);
      else toast.error(data.reason || "Email failed (check RESEND_API_KEY)");
    } catch (e) { toast.error("Email failed"); }
    setEmailing(false);
  };

  useEffect(() => { run(); }, []);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(run, 60_000);  // every 60s
    return () => clearInterval(t);
  }, [autoRefresh]);

  return (
    <div className="space-y-5" data-testid="health-check-tab">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow text-nexus-mint">// platform health · daily sweep</div>
          <h2 className="font-display font-black text-3xl mt-1">All systems</h2>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-[10px] tracking-wider text-white/50">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="accent-nexus-cyan" data-testid="health-auto" />
            Auto-refresh every 60s
          </label>
          <button onClick={run} disabled={loading} className="btn-ghost text-[10px]" data-testid="health-run">
            {loading ? "Running…" : "Run now"}
          </button>
          <button onClick={emailNow} disabled={emailing} className="btn-nexus text-[10px]" data-testid="health-email">
            {emailing ? "Sending…" : "📧 Email me this report"}
          </button>
        </div>
      </div>

      <div className="bg-nexus-surface border border-white/10 p-4 space-y-3" data-testid="contact-email-block">
        <div className="flex items-center gap-2">
          <div className="label-eyebrow text-nexus-cyan">// contact email (where reports & digests are sent)</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="you@gmail.com"
            className="flex-1 min-w-[260px] bg-transparent border border-white/10 px-3 py-2 text-sm"
            data-testid="contact-email-input"
          />
          <button onClick={saveContactEmail} disabled={savingEmail} className="btn-ghost text-[10px]" data-testid="save-contact-email">
            {savingEmail ? "Saving…" : "Save"}
          </button>
        </div>
        <div className="text-[10px] text-white/40">Gmail SMTP is wired — health + parental digest emails go out the moment you click their respective email buttons.</div>
      </div>

      {!health && <div className="text-white/40">Loading…</div>}
      {bounty && (
        <div className="grid sm:grid-cols-4 gap-px bg-white/5" data-testid="owner-bounty-stats">
          <div className="bg-nexus-surface p-4">
            <div className="label-eyebrow">your nx balance</div>
            <div className="font-display text-3xl mt-1 text-nexus-amber">{(bounty.owner_balance || 0).toFixed(0)}</div>
          </div>
          <div className="bg-nexus-surface p-4">
            <div className="label-eyebrow">bounties gifted</div>
            <div className="font-display text-3xl mt-1 text-nexus-mint">{(bounty.bounties_paid?.count || 0).toLocaleString()}</div>
            <div className="text-[10px] text-white/40 mt-1">{(bounty.bounty_paid_total_nx || 0).toFixed(0)} NX out of your wallet</div>
          </div>
          <div className="bg-nexus-surface p-4">
            <div className="label-eyebrow">latest recipient</div>
            <div className="font-display text-base mt-1 truncate">{bounty.recent?.[0] ? `+${bounty.recent[0].amount_nx} NX` : "—"}</div>
            <div className="text-[10px] text-white/40 mt-1">{bounty.recent?.[0]?.ts?.split("T")[0] || ""}</div>
          </div>
          <div className="bg-nexus-surface p-4">
            <div className="label-eyebrow">recent (10)</div>
            <div className="font-mono text-[10px] mt-1 space-y-0.5 max-h-16 overflow-y-auto">
              {(bounty.recent || []).slice(0, 6).map((r) => (
                <div key={r.id} className="truncate text-white/60">+{r.amount_nx} NX · milestone {r.milestone}</div>
              ))}
            </div>
          </div>
        </div>
      )}
      {health && (
        <>
          <div className={`p-4 border-l-2 ${health.ok ? "border-nexus-mint bg-nexus-mint/5" : "border-nexus-crimson bg-nexus-crimson/5"}`}>
            <div className="text-[11px] tracking-[0.3em] uppercase font-display" style={{ color: health.ok ? "#00FF66" : "#FF3366" }}>
              {health.ok ? "● ALL GREEN" : "● NEEDS ATTENTION"}
            </div>
            <div className="text-[10px] text-white/40 mt-1">Generated {new Date(health.generated_at).toLocaleString()}</div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5" data-testid="health-grid">
            {health.checks.map((c) => (
              <div key={c.id} className="bg-nexus-surface p-4" data-testid={`health-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">{c.label}</div>
                  <div className={`text-[10px] tracking-wider font-display px-2 py-0.5 border ${c.ok ? "border-nexus-mint text-nexus-mint" : "border-nexus-crimson text-nexus-crimson"}`}>
                    {c.ok ? "OK" : "FAIL"}
                  </div>
                </div>
                {"count" in c && (
                  <div className="font-display font-black text-2xl mt-2" style={{ color: c.ok ? "#00F0FF" : "#FF3366" }}>{c.count.toLocaleString()}</div>
                )}
                {c.error && <div className="text-[10px] text-nexus-crimson mt-1">{c.error}</div>}
                {c.mode && <div className="text-[10px] text-white/40 mt-1">mode: {c.mode}</div>}
                {c.expected !== undefined && !c.ok && <div className="text-[10px] text-white/50 mt-1">expected ≥ {c.expected}</div>}
              </div>
            ))}
          </div>

          <div className="text-[10px] text-white/40 leading-relaxed pt-2 border-t border-white/10">
            Tip — set up a daily cron (or use the &ldquo;Email me this report&rdquo; button after morning coffee) to keep a paper trail. Gmail SMTP powers email delivery; configure <code className="text-nexus-amber">GMAIL_USER</code> + <code className="text-nexus-amber">GMAIL_APP_PASSWORD</code> in .env to enable.
          </div>
        </>
      )}
    </div>
  );
}

/* ============================== AD ESCROWS ============================== */
// Round K Phase C1 — real-time view of every paid ad campaign:
//   · 40% platform share (paid to owner PayPal · retry button if payout failed)
//   · 60% creator+viewer escrow (held, distributed at settlement)
//   · live-accumulating creator + viewer leaderboards while the campaign runs
//   · once the duration_days is up, the leaderboards freeze and Settle Now is enabled
function AdEscrowsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/owner/ad-escrows");
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load escrows"); }
    setLoading(false);
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  const retryPayout = async (cid) => {
    try {
      const { data } = await api.post(`/owner/ad-escrows/${cid}/retry-payout`);
      if (data.ok) toast.success("Payout re-sent to owner PayPal");
      else {
        const r = data.response || {};
        const msg = r.detail || r.error || r.message || JSON.stringify(r).slice(0, 160);
        toast.error(`Payout retry failed — ${msg}`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
  };
  const retryAllPending = async () => {
    if (bulkBusy) return;
    const pending = items.filter((c) => c.owner_payout_pending);
    if (!pending.length) { toast.info("No pending payouts to retry"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/owner/ad-escrows/retry-all-pending");
      const nOk = (data.succeeded || []).length;
      const nFail = (data.failed || []).length;
      if (nOk && !nFail) toast.success(`All ${nOk} pending payouts cleared`);
      else if (nOk && nFail) toast.success(`${nOk} paid · ${nFail} still failing — check each row`);
      else toast.error(`${nFail} payouts still failing — check PayPal Payouts config`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk retry failed"); }
    setBulkBusy(false);
  };
  const settleNow = async (cid) => {
    try {
      const { data } = await api.post(`/business/campaigns/${cid}/settle`);
      toast.success(`Settled — creators paid $${data.creator_paid_aud} · viewers paid $${data.viewer_paid_aud}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Settle failed"); }
  };
  if (loading) return <div className="text-white/60 p-8">Loading escrows…</div>;
  if (!items.length) return (
    <div className="glass p-8 text-center" data-testid="ad-escrows-empty">
      <div className="label-eyebrow">// ad escrows</div>
      <h2 className="font-display font-black text-2xl mt-2">No paid campaigns yet</h2>
      <p className="text-sm text-white/50 mt-2">Every ad campaign that clears the PayPal Pay-Now flow lands here — with its 40% platform payout, 60% escrow, and live leaderboards until settlement.</p>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="ad-escrows-tab">
      {(() => {
        const pending = items.filter((c) => c.owner_payout_pending);
        if (!pending.length) return null;
        const totalDue = pending.reduce((a, c) => a + (c.platform_paid_aud || c.budget_aud * 0.40 || 0), 0);
        return (
          <div
            className="glass p-4 flex items-center gap-4 flex-wrap border border-nexus-amber/30"
            style={{ backgroundColor: "rgba(255, 193, 7, 0.08)" }}
            data-testid="ad-escrows-pending-banner"
          >
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(255, 193, 7, 0.2)" }}>
              <AlertCircle className="w-5 h-5 text-nexus-amber" />
            </div>
            <div className="min-w-0">
              <div className="label-eyebrow text-nexus-amber">// pending owner payouts</div>
              <div className="font-display font-bold text-base mt-0.5">
                {pending.length} campaign{pending.length === 1 ? "" : "s"} · ${totalDue.toFixed(2)} AUD waiting
              </div>
              <div className="text-[11px] text-white/50 mt-0.5">
                The 40% platform share is held in the Nexus PayPal merchant balance — tap retry to re-send to the owner PayPal.
              </div>
            </div>
            <button
              onClick={retryAllPending}
              disabled={bulkBusy}
              className="btn-nexus !py-1.5 !px-4 text-[10px] ml-auto"
              data-testid="retry-all-pending-payouts"
            >
              {bulkBusy ? "Retrying…" : `Retry all ${pending.length}`}
            </button>
          </div>
        );
      })()}
      <div className="glass p-4 flex items-center gap-4 flex-wrap">
        <Target className="w-5 h-5 text-nexus-cyan" />
        <div>
          <div className="label-eyebrow">// active ad escrows</div>
          <div className="text-xs text-white/50">Auto-refreshes every 20s while a campaign is running. Only you (owner) sees the leaderboards until settlement.</div>
        </div>
        <div className="ml-auto text-[10px] tracking-widest text-white/40 font-mono">{items.length} campaigns</div>
      </div>
      {items.map((c) => (
        <div key={c.id} className="glass p-5 space-y-4" data-testid={`ad-escrow-${c.id}`}>
          {/* Header */}
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <div className="font-display font-bold text-lg truncate">{c.name}</div>
              <div className="text-[11px] text-white/40 mt-0.5 font-mono">by {c.owner_name || c.owner_id?.slice(0, 8)} · {(c.status || "").toUpperCase()} · paid {c.paid_at ? new Date(c.paid_at).toLocaleString() : "—"}</div>
            </div>
            <div className="flex items-center gap-2">
              {c.owner_payout_pending && (
                <button onClick={() => retryPayout(c.id)} className="btn-ghost !py-1.5 !px-3 text-[10px] text-nexus-amber" data-testid={`retry-payout-${c.id}`}>
                  Retry 40% payout
                </button>
              )}
              {(c.status === "ended" || c.auto_settled_at) && !c.settled && (
                <button onClick={() => settleNow(c.id)} className="btn-nexus !py-1.5 !px-3 text-[10px]" data-testid={`settle-${c.id}`}>
                  Settle now
                </button>
              )}
            </div>
          </div>
          {/* Money split */}
          <div className="grid grid-cols-3 gap-px bg-white/5">
            <div className="bg-nexus-surface p-3">
              <div className="label-eyebrow text-nexus-mint">// 40% platform</div>
              <div className="font-display font-black text-xl mt-1" style={{ color: c.owner_payout_ok ? "#00FF66" : "#FFC107" }}>${(c.platform_paid_aud || 0).toFixed(2)}</div>
              <div className="text-[10px] text-white/40 mt-1">{c.owner_payout_ok ? "✓ paid to owner PayPal" : "⚠ payout pending"}</div>
            </div>
            <div className="bg-nexus-surface p-3">
              <div className="label-eyebrow text-nexus-cyan">// 60% escrow</div>
              <div className="font-display font-black text-xl mt-1 text-nexus-cyan">${(c.escrow_60_aud || 0).toFixed(2)}</div>
              <div className="text-[10px] text-white/40 mt-1">held · creators 40% + viewers 20%</div>
            </div>
            <div className="bg-nexus-surface p-3">
              <div className="label-eyebrow">// impressions</div>
              <div className="font-display font-black text-xl mt-1">{(c.impressions || 0).toLocaleString()}</div>
              <div className="text-[10px] text-white/40 mt-1">{c.unique_viewers || 0} unique viewers · {c.clicks || 0} clicks</div>
            </div>
          </div>
          {/* Leaderboards */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="label-eyebrow flex items-center gap-2 mb-2">
                <span className="text-nexus-pink">// creators leaderboard</span>
                <span className="text-white/40">({c.creators_leaderboard?.length || 0})</span>
              </div>
              <div className="space-y-1 bg-white/5 p-2 max-h-64 overflow-y-auto">
                {(c.creators_leaderboard || []).length === 0 && <div className="text-xs text-white/40 p-3 text-center">No creators have surfaced this ad yet.</div>}
                {(c.creators_leaderboard || []).map((r, i) => (
                  <div key={r.user_id} className="flex items-center gap-2 p-1.5 hover:bg-white/5 rounded text-xs">
                    <div className="w-5 text-white/40 font-mono">{i + 1}.</div>
                    <img src={r.avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${r.user_id}`} alt="" className="w-6 h-6 rounded-full" />
                    <div className="flex-1 truncate">{r.display_name}</div>
                    <div className="text-white/50">{r.unique_viewers} views</div>
                    <div className="text-nexus-mint font-mono w-16 text-right">${r.projected_aud}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="label-eyebrow flex items-center gap-2 mb-2">
                <span className="text-nexus-cyan">// viewers leaderboard</span>
                <span className="text-white/40">({c.viewers_leaderboard?.length || 0})</span>
              </div>
              <div className="space-y-1 bg-white/5 p-2 max-h-64 overflow-y-auto">
                {(c.viewers_leaderboard || []).length === 0 && <div className="text-xs text-white/40 p-3 text-center">No viewers have seen this ad yet.</div>}
                {(c.viewers_leaderboard || []).map((r, i) => (
                  <div key={r.user_id} className="flex items-center gap-2 p-1.5 hover:bg-white/5 rounded text-xs">
                    <div className="w-5 text-white/40 font-mono">{i + 1}.</div>
                    <img src={r.avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${r.user_id}`} alt="" className="w-6 h-6 rounded-full" />
                    <div className="flex-1 truncate">{r.display_name}</div>
                    <div className="text-white/50">{r.distinct_creators}/5 surfaces</div>
                    <div className="text-nexus-cyan font-mono w-16 text-right">${r.projected_aud}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
      <PayoutAuditSection />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payout Audit Log — every 40% owner-payout attempt (initial, retry, bulk)
// with timestamps, PayPal batch id, success/fail + error detail.
// ---------------------------------------------------------------------------
function PayoutAuditSection() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, succeeded: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/owner/payout-audit?limit=200");
      setRows(data.items || []);
      setStats({ total: data.total || 0, succeeded: data.succeeded || 0, failed: data.failed || 0 });
    } catch (e) { /* ignore */ }
    setLoading(false);
  };
  const downloadCsv = async () => {
    try {
      const res = await api.get("/owner/payout-audit.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `nexus-payout-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${stats.total} payout attempt${stats.total === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "CSV export failed"); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  return (
    <div className="glass p-5" data-testid="payout-audit-section">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 text-left"
        data-testid="payout-audit-toggle"
      >
        <Activity className="w-5 h-5 text-nexus-cyan" />
        <div className="flex-1">
          <div className="label-eyebrow">// payout audit log</div>
          <div className="text-sm text-white/70 mt-0.5">
            Every 40% owner-payout attempt with timestamp + PayPal batch id + error detail
          </div>
        </div>
        {open && (
          <div className="text-[10px] font-mono text-white/50 flex items-center gap-3 mr-2">
            <span>total <span className="text-white">{stats.total}</span></span>
            <span className="text-nexus-mint">ok {stats.succeeded}</span>
            <span className="text-nexus-amber">fail {stats.failed}</span>
          </div>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-4" data-testid="payout-audit-body">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-mono text-white/50">
              Showing newest {Math.min(rows.length, 200)} of {stats.total} attempts
            </div>
            <button
              onClick={downloadCsv}
              disabled={!rows.length}
              className="btn-ghost !py-1.5 !px-3 text-[10px] disabled:opacity-40"
              data-testid="payout-audit-csv-export"
            >
              Download CSV
            </button>
          </div>
          {loading && <div className="text-xs text-white/50 py-3">Loading audit log…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-xs text-white/40 py-3">No payout attempts recorded yet. The log fills up automatically after the first ad campaign is paid.</div>
          )}
          {!loading && rows.length > 0 && (
            <div className="bg-white/5 rounded overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-white/40 border-b border-white/10">
                    <th className="px-3 py-2 font-mono">When</th>
                    <th className="px-3 py-2 font-mono">Campaign</th>
                    <th className="px-3 py-2 font-mono">Type</th>
                    <th className="px-3 py-2 font-mono text-right">AUD</th>
                    <th className="px-3 py-2 font-mono">Status</th>
                    <th className="px-3 py-2 font-mono">Batch id / error</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`payout-attempt-${r.id}`}>
                      <td className="px-3 py-2 font-mono text-white/60 whitespace-nowrap">{new Date(r.ts).toLocaleString()}</td>
                      <td className="px-3 py-2 truncate max-w-[180px]" title={r.campaign_name}>{r.campaign_name || r.campaign_id?.slice(0, 8)}</td>
                      <td className="px-3 py-2 font-mono text-white/60">{r.attempt_type}</td>
                      <td className="px-3 py-2 font-mono text-right">${(r.amount_aud || 0).toFixed(2)}</td>
                      <td className="px-3 py-2">
                        {r.success ? (
                          <span className="text-nexus-mint font-mono">✓ {r.batch_status || "paid"}</span>
                        ) : (
                          <span className="text-nexus-amber font-mono">✗ failed</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-white/50 truncate max-w-[260px]" title={r.batch_id || r.error_detail}>
                        {r.batch_id || r.error_detail || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

